import { ROLES } from "@user-management-context/shared/domain/types/enums/roles";
import type { UserDetailsRoleVM } from "@user-management-context/shared/domain/types/user-details-role-vm";

/**
 * Active role storage uses two mechanisms:
 * - sessionStorage (per-tab): each browser tab keeps its own active role, so a user
 *   can work as TEACHER in one tab and PROVIDER in another without interference.
 *   The HTTP interceptor reads from here to set the X-Active-Role header.
 * - localStorage (cross-session): remembers the last role used across all tabs,
 *   so the user reconnects with their most recent role after a new login.
 * Admin users (isAdmin flag on the user) have no business role; their active role may be null.
 */
export const ACTIVE_ROLE_STORAGE_KEY = "activeRole";

type RoleList = ReadonlyArray<UserDetailsRoleVM | ROLES> | null | undefined;

const parseRole = (value: string | null): ROLES | null => {
  if (!value) return null;
  return Object.values(ROLES).includes(value as ROLES) ? (value as ROLES) : null;
};

export const hasRole = (roles: RoleList, role: ROLES): boolean => {
  if (!roles) return false;
  return roles.some(r => (typeof r === "string" ? r : r.role) === role);
};

export const getActiveRole = (): ROLES | null => {
  if (typeof sessionStorage === "undefined") return null;
  return parseRole(sessionStorage.getItem(ACTIVE_ROLE_STORAGE_KEY));
};

export const getLastActiveRole = (): ROLES | null => {
  if (typeof localStorage === "undefined") return null;
  return parseRole(localStorage.getItem(ACTIVE_ROLE_STORAGE_KEY));
};

/**
 * User-initiated role choice. Writes the role (or clears it) in BOTH stores so
 * the per-tab active role and the cross-session preference are updated together.
 * Use this at call sites where the user is making a deliberate choice (login,
 * role-switcher, onboarding).
 */
export const writeActiveRoleToStorage = (role: ROLES | null): void => {
  if (role === null) {
    if (typeof sessionStorage !== "undefined") sessionStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
    if (typeof localStorage !== "undefined") localStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
    return;
  }
  if (typeof sessionStorage !== "undefined") sessionStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, role);
  if (typeof localStorage !== "undefined") localStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, role);
};

/**
 * Sync-time restore — only touches sessionStorage so a tab's per-tab role can be
 * re-established without overwriting the cross-session memory used by other tabs.
 *
 * Asymmetric null contract: passing null clears sessionStorage ONLY and leaves
 * localStorage untouched. This preserves the "last role used across tabs"
 * preference when an admin session is restored or when the current tab simply
 * has no remaining valid role to restore.
 */
export const restoreActiveRole = (role: ROLES | null): void => {
  if (typeof sessionStorage === "undefined") return;
  if (role === null) {
    sessionStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
    return;
  }
  sessionStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, role);
};
